import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Phone, PhoneOff, Mic, MicOff, Volume2, VolumeX, Video, Clock, User } from 'lucide-react';
import { useCRMAuth } from '../../contexts/CRMAuthContext';
import { useWebRTCContext } from '../WebRTC/WebRTCContext';
import { useToast } from '../../hooks/use-toast';

const VoIPCaller = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [isVideoCall, setIsVideoCall] = useState(false);
  const [error, setError] = useState('');
  const [voipToken, setVoipToken] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  // WebRTC target when calling a resolved same-business app-user contact (call by app_user_id, not phone).
  const [targetAppUserId, setTargetAppUserId] = useState('');
  
  const { user, makeAuthenticatedRequest } = useCRMAuth();
  const callTimerRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);

  // Use WebRTC hook
  const {
    isConnected,
    currentCall,
    localStream,
    remoteStream,
    callStatus,
    initiateCall,
    endCall,
    toggleMute,
    getMediaStream
  } = useWebRTCContext();   // shared singleton — no second socket

  // Handle URL query parameters
  useEffect(() => {
    const phoneParam = searchParams.get('phone');
    const nameParam = searchParams.get('name');
    
    if (phoneParam) {
      setPhoneNumber(phoneParam);
      if (nameParam) {
        setBusinessName(nameParam);
      }
    }
  }, [searchParams]);

  // Handle navigation state
  useEffect(() => {
    if (location.state) {
      const { phoneNumber: statePhone, appUserId: stateAppUserId, businessName: stateBusiness, autoCall, isVideoCall: stateIsVideo } = location.state;
      if (statePhone) setPhoneNumber(statePhone);
      if (stateAppUserId) setTargetAppUserId(stateAppUserId);
      if (statePhone || stateAppUserId) {
        setBusinessName(stateBusiness || '');
        setIsVideoCall(stateIsVideo || false);

        if (autoCall && isConnected) {
          // Pass the target explicitly (avoids a stale-closure read of freshly-set state).
          const t = stateAppUserId || statePhone;
          setTimeout(() => {
            doCall(t, !!stateIsVideo, !!stateAppUserId);
          }, 1000);
        }
      }
    }
  }, [location.state, isConnected]);

  // Setup video elements when streams are available
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  // WebRTC connection + token initialization
  useEffect(() => {
    setIsInitialized(isConnected);

    // Fetch VoIP token when connected
    if (isConnected && !voipToken && makeAuthenticatedRequest) {
      makeAuthenticatedRequest('/api/v1/voip/token', { method: 'GET' })
        .then(res => {
          if (res.ok) return res.json();
          return null;
        })
        .then(data => {
          if (data?.token) {
            setVoipToken(data.token);
          }
        })
        .catch(err => {
          console.warn('VoIP token fetch failed (non-critical):', err);
        });
    }
  }, [isConnected]);

  // Call timer
  useEffect(() => {
    if (callStatus === 'connected') {
      callTimerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    } else {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
      if (callStatus === 'idle') {
        setCallDuration(0);
      }
    }

    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
    };
  }, [callStatus]);

  // Dialpad numbers
  const dialpadNumbers = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['+', '0', '#']
  ];

  const handleDialpadClick = (number) => {
    setPhoneNumber(prev => prev + number);
  };

  // Real data only — no mock. Recent calls stay empty (honest) until call-history wiring; contacts
  // are loaded from the real owner-scoped /contacts endpoint below.
  const [recentCalls, setRecentCalls] = useState([]);
  const [contacts, setContacts] = useState([]);

  // Load the caller's real contacts (owner-scoped) for the Contacts tab. No fabricated data.
  useEffect(() => {
    if (!makeAuthenticatedRequest) return;
    let active = true;
    (async () => {
      try {
        const resp = await makeAuthenticatedRequest('/api/v1/contacts/', { method: 'GET' });
        if (!resp.ok) { if (active) setContacts([]); return; }
        const data = await resp.json();
        if (!active) return;
        const list = Array.isArray(data) ? data : [];
        setContacts(list.map((c) => ({
          id: c.id,
          name: c.display_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.phone_number || 'Contact',
          number: c.phone_number || '',
          company: c.company || '',
          is_app_user: !!c.is_app_user,
          app_user_id: c.app_user_id || null,
        })));
      } catch { if (active) setContacts([]); }
    })();
    return () => { active = false; };
  }, [makeAuthenticatedRequest]);

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits && !value.startsWith('+')) {
      return '+' + digits;
    }
    return value;
  };

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^\+[1-9]\d{1,14}$/;
    return phoneRegex.test(phone);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'bg-green-500';
      case 'ringing': return 'bg-yellow-500';
      case 'ended': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'ringing': return 'Calling...';
      case 'connected': return 'Connected';
      case 'ended': return 'Call Ended';
      default: return 'Ready to Call';
    }
  };

  // Core call routine. targetUserId is either a same-business app_user_id (isAppUser=true) or a phone
  // number. Phone-format validation only applies when calling by number — an app_user_id is a uid.
  const doCall = async (targetUserId, video, isAppUser) => {
    if (!targetUserId) {
      toast({
        title: "Cannot Make Call",
        description: isAppUser ? "No call target for this contact" : "Please enter a phone number",
        variant: "destructive"
      });
      return;
    }

    if (!isAppUser && !validatePhoneNumber(targetUserId)) {
      setError('Please enter a valid phone number in E.164 format (e.g., +1234567890)');
      return;
    }

    setError('');

    try {
      // Request media access before calling
      if (video) {
        try {
          await getMediaStream(true);
        } catch (mediaErr) {
          console.warn('Could not get video stream, falling back to audio:', mediaErr);
          setIsVideoCall(false);
          video = false;
        }
      }

      await initiateCall(targetUserId, video ? 'video' : 'audio');

      toast({
        title: "Call Initiated",
        description: `Calling ${businessName || targetUserId}...`,
      });
    } catch (error) {
      console.error('Call initiation error:', error);
      setError(error.message || 'Failed to initiate call');
      toast({
        title: "Call Failed",
        description: error.message || "Failed to initiate call. Please check WebRTC connection.",
        variant: "destructive"
      });
    }
  };

  // Button handler (manual dial pad / contact caller). Prefer the resolved app_user_id target.
  const handleInitiateCall = () =>
    doCall(targetAppUserId || phoneNumber, isVideoCall, !!targetAppUserId);

  const handleEndCall = () => {
    if (currentCall) {
      endCall();
      toast({
        title: "Call Ended",
        description: "Call has been disconnected",
      });
    }
  };

  const handleToggleMute = () => {
    toggleMute();
    setIsMuted(!isMuted);
  };

  const handleToggleSpeaker = () => {
    setIsSpeakerOn(!isSpeakerOn);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!user) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p className="text-gray-500">Please log in to use VoIP calling</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-md mx-auto space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <Phone className="w-5 h-5 mr-2" />
              VoIP Caller
            </div>
            <div className="flex items-center space-x-2">
              {isConnected ? (
                <Badge className="bg-green-500" data-testid="webrtc-status">
                  WebRTC Connected
                </Badge>
              ) : (
                <Badge variant="outline" className="border-yellow-500 text-yellow-700" data-testid="webrtc-status">
                  Connecting...
                </Badge>
              )}
              {callStatus !== 'idle' && (
                <Badge className={getStatusColor(callStatus)} data-testid="call-status-badge">
                  {getStatusText(callStatus)}
                </Badge>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {error && (
            <Alert className="border-red-200 bg-red-50">
              <AlertDescription className="text-red-800">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {!isInitialized && (
            <Alert className="border-yellow-200 bg-yellow-50">
              <AlertDescription className="text-yellow-800">
                Initializing VoIP service{voipToken ? ' (token acquired)' : ''}...
              </AlertDescription>
            </Alert>
          )}

          {callStatus === 'idle' && (
            <Tabs defaultValue="dialpad" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="dialpad">Dialpad</TabsTrigger>
                <TabsTrigger value="recent">Recent</TabsTrigger>
                <TabsTrigger value="contacts">Contacts</TabsTrigger>
              </TabsList>

              {/* Dialpad Tab */}
              <TabsContent value="dialpad" className="space-y-4">
                <div>
                  <Input
                    type="tel"
                    placeholder="+1 234 567 8900"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                    className="text-lg text-center mb-4"
                    data-testid="phone-input"
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-2">
                  {dialpadNumbers.flat().map((number) => (
                    <Button
                      key={number}
                      variant="outline"
                      onClick={() => handleDialpadClick(number)}
                      className="h-14 text-xl"
                    >
                      {number}
                    </Button>
                  ))}
                </div>
                
                <Button
                  onClick={handleInitiateCall}
                  disabled={!phoneNumber}
                  className="w-full bg-green-600 hover:bg-green-700"
                  data-testid="call-btn"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Call
                </Button>
              </TabsContent>

              {/* Recent Calls Tab */}
              <TabsContent value="recent" className="space-y-2">
                {recentCalls.length === 0 && (
                  <p className="text-center text-gray-500 py-6 text-sm">No recent calls yet.</p>
                )}
                {recentCalls.map((call) => (
                  <div key={call.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => setPhoneNumber(call.number)}>
                    <div className="flex items-center space-x-3">
                      <User className="h-5 w-5 text-gray-400" />
                      <div>
                        <div className="font-medium">{call.name}</div>
                        <div className="text-sm text-gray-600">{call.number}</div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500 flex items-center space-x-2">
                      <Clock className="h-4 w-4" />
                      <span>{call.time}</span>
                    </div>
                  </div>
                ))}
              </TabsContent>

              {/* Contacts Tab */}
              <TabsContent value="contacts" className="space-y-2">
                {contacts.length === 0 && (
                  <p className="text-center text-gray-500 py-6 text-sm">No contacts yet.</p>
                )}
                {contacts.map((contact) => (
                  <div key={contact.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => setPhoneNumber(contact.number)}>
                    <div className="flex items-center space-x-3">
                      <User className="h-5 w-5 text-gray-400" />
                      <div>
                        <div className="font-medium">{contact.name}</div>
                        <div className="text-sm text-gray-600">{contact.company}</div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPhoneNumber(contact.number);
                        handleInitiateCall();
                      }}
                    >
                      <Phone className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </TabsContent>
            </Tabs>
          )}

          {(callStatus === 'ringing' || callStatus === 'connected') && (
            <div className="space-y-4">
              {/* Video elements for video calls */}
              {isVideoCall && (
                <div className="relative bg-gray-900 rounded-lg overflow-hidden" style={{ height: '400px' }}>
                  <video
                    ref={remoteVideoRef}
                    autoPlay
                    playsInline
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 right-4 w-32 h-24 bg-gray-800 rounded-lg overflow-hidden border-2 border-white shadow-lg">
                    <video
                      ref={localVideoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              )}

              <div className="text-center space-y-4">
                <div>
                  <p className="text-lg font-medium">{businessName || phoneNumber}</p>
                  <Badge className={`mt-1 ${getStatusColor(callStatus)}`}>
                    {getStatusText(callStatus)}
                  </Badge>
                  {callStatus === 'connected' && (
                    <p className="text-2xl font-mono text-blue-600 mt-2">
                      {formatDuration(callDuration)}
                    </p>
                  )}
                </div>

                {callStatus === 'connected' && (
                  <div className="flex justify-center space-x-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleToggleMute}
                      className={isMuted ? 'bg-red-100 border-red-300' : ''}
                      data-testid="mute-btn"
                    >
                      {isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleToggleSpeaker}
                      className={isSpeakerOn ? 'bg-blue-100 border-blue-300' : ''}
                      data-testid="speaker-btn"
                    >
                      {isSpeakerOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                    </Button>

                    {isVideoCall && (
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid="video-toggle-btn"
                      >
                        <Video className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                )}

                <Button
                  onClick={handleEndCall}
                  className="w-full bg-red-600 hover:bg-red-700"
                  data-testid="end-call-btn"
                >
                  <PhoneOff className="w-4 h-4 mr-2" />
                  End Call
                </Button>
              </div>
            </div>
          )}

          {callStatus === 'ended' && (
            <div className="text-center space-y-4">
              <p className="text-gray-600">Call ended</p>
              <p className="text-sm text-gray-500">
                Duration: {formatDuration(callDuration)}
              </p>
              <div className="flex space-x-2 justify-center">
                <Button
                  variant="outline"
                  onClick={() => navigate(-1)}
                  data-testid="back-after-call-btn"
                >
                  Back
                </Button>
                <Button
                  onClick={() => {
                    setPhoneNumber('');
                    setBusinessName('');
                    setError('');
                  }}
                  data-testid="new-call-btn"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  New Call
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Call Statistics */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Quick Stats</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Status</p>
              <p className="font-medium capitalize">{user?.voip_enabled ? 'Enabled' : 'Disabled'}</p>
            </div>
            <div>
              <p className="text-gray-500">Quality</p>
              <p className="font-medium text-green-600">Good</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VoIPCaller;